<section hlmCard>
  <div hlmCardHeader>
    <h2 hlmCardTitle>Seite</h2>
    <p hlmCardDescription>Gedruckt: {{ printedLabel() }}</p>
  </div>
  <div hlmCardContent class="space-y-4">
    <div class="space-y-2">
      <span hlmLabel>Hintergrund</span>
      <div class="flex items-center gap-2 text-sm">
        @if (design().background_kind === 'none') {
          <span
            class="inline-block size-5 shrink-0 rounded border"
            [style.background]="design().background_color"
            aria-hidden="true"></span>
        }
        <span class="min-w-0 truncate">{{ backgroundLabel() }}</span>
      </div>
      <div class="flex flex-wrap gap-2">
        <button
          hlmBtn
          variant="outline"
          size="sm"
          type="button"
          [disabled]="busy()"
          (click)="openFilePicker()">
          <ng-icon name="lucideFileUp" />
          {{ hasUpload() ? 'Datei ersetzen…' : 'Datei…' }}
        </button>
        <button
          hlmBtn
          variant="outline"
          size="sm"
          type="button"
          [disabled]="busy()"
          (click)="pickTemplate.emit()">
          <ng-icon name="lucideLayoutTemplate" />
          Vorlage…
        </button>
        @if (design().background_kind !== 'none') {
          <button
            hlmBtn
            variant="ghost"
            size="sm"
            type="button"
            [disabled]="busy()"
            (click)="removeBackground.emit()">
            <ng-icon name="lucideX" />
            Entfernen
          </button>
        }
      </div>
      <input
        #fileInput
        type="file"
        class="sr-only"
        accept="application/pdf,image/svg+xml,image/png,image/jpeg,image/webp,image/heic,image/heif,.pdf,.svg,.png,.jpg,.jpeg,.webp,.heic,.heif"
        aria-label="Hintergrund-Datei auswählen"
        (change)="onFileSelected($event)" />
      @if (design().background_kind === 'none') {
        <div class="flex items-center gap-2">
          <label hlmLabel for="page-color">Seitenfarbe</label>
          <app-color-field
            id="page-color"
            label="Seitenfarbe"
            [value]="design().background_color"
            [disabled]="busy()"
            (valueChange)="onPageColor($event)" />
        </div>
      }
      <p hlmMuted class="text-xs">
        Eine Datei bringt ihre eigene Seitengröße mit; die Elemente bleiben, wo sie sind.
        @if (dirty()) {
          Ungespeicherte Änderungen werden vorher gespeichert.
        }
      </p>
    </div>

    <div class="space-y-1">
      <span hlmLabel id="page-orientation">Ausrichtung</span>
      <div class="flex gap-2" role="group" aria-labelledby="page-orientation">
        <button
          hlmBtn
          size="sm"
          type="button"
          [variant]="!landscape() ? 'default' : 'outline'"
          [attr.aria-pressed]="!landscape()"
          [disabled]="busy() || !canTurn()"
          (click)="turn(false)">
          <ng-icon name="lucideRectangleVertical" />
          Hochformat
        </button>
        <button
          hlmBtn
          size="sm"
          type="button"
          [variant]="landscape() ? 'default' : 'outline'"
          [attr.aria-pressed]="landscape()"
          [disabled]="busy() || !canTurn()"
          (click)="turn(true)">
          <ng-icon name="lucideRectangleHorizontal" />
          Querformat
        </button>
      </div>
      @if (!canTurn()) {
        <p hlmMuted class="text-xs">
          {{ hasUpload() ? 'Die Datei' : 'Die Vorlage' }} gibt die Ausrichtung vor.
        </p>
      }
    </div>

    <div class="grid grid-cols-2 gap-3">
      <div class="space-y-1">
        <label hlmLabel for="page-w">Breite (mm)</label>
        <input
          hlmInput
          id="page-w"
          type="number"
          min="1"
          step="1"
          [value]="draftWidthMm()"
          (change)="onWidthMm($event)" />
      </div>
      <div class="space-y-1">
        <label hlmLabel for="page-h">Höhe (mm)</label>
        <input
          hlmInput
          id="page-h"
          type="number"
          min="1"
          step="1"
          [value]="draftHeightMm()"
          (change)="onHeightMm($event)" />
      </div>
    </div>
    <div class="space-y-1">
      <label hlmLabel for="page-pct">{{
        hasUpload() ? 'Größe der hochgeladenen Datei (%)' : 'Größe (%)'
      }}</label>
      <input
        hlmInput
        id="page-pct"
        type="number"
        min="1"
        step="1"
        [value]="draftPercent()"
        (change)="onPercent($event)" />
      <p hlmMuted class="text-xs">
        {{ hasUpload() ? 'Hochgeladen' : 'Ausgangsgröße' }}: {{ sourceLabel() }} · möglich:
        {{ rangeLabel() }}
      </p>
    </div>

    <div class="space-y-1">
      <span hlmLabel>Papierformat</span>
      <div class="flex flex-wrap gap-2">
        @for (preset of presets(); track preset.label) {
          <button
            hlmBtn
            size="sm"
            type="button"
            [variant]="draft() === preset.scale ? 'default' : 'outline'"
            [disabled]="!preset.possible"
            [attr.title]="preset.hint"
            (click)="draft.set(preset.scale)">
            {{ preset.label }}
          </button>
        }
      </div>
      <p hlmMuted class="text-xs">
        Die Seite wird ins Format eingepasst, ihr Seitenverhältnis bleibt erhalten.
      </p>
    </div>

    @if (!inRange()) {
      <p class="text-destructive text-sm">Jede Seite muss zwischen 5 cm und 105 cm liegen.</p>
    }
    <p hlmMuted class="text-xs">
      Alle Elemente werden mitskaliert, auch ihre Schriftgröße.
      @if (dirty()) {
        Ungespeicherte Änderungen werden vorher gespeichert.
      }
    </p>

    <div class="flex flex-wrap gap-2">
      <button
        hlmBtn
        size="sm"
        type="button"
        [disabled]="!changed() || !inRange() || busy()"
        (click)="apply()">
        @if (busy()) {
          <hlm-spinner />
        } @else {
          <ng-icon name="lucideRuler" />
        }
        Druckgröße übernehmen
      </button>
      <button
        hlmBtn
        variant="outline"
        size="sm"
        type="button"
        [disabled]="busy()"
        (click)="draft.set(1)">
        <ng-icon name="lucideRotateCcw" />
        100 %
      </button>
    </div>
    <p hlmMuted class="text-xs">Klicke auf ein Element, um es zu bearbeiten.</p>
  </div>
</section>
